/**
 * Which connected system keeps each scoring attribute in this workspace (read once from the
 * workspace's connections; src/lib/itemFieldSources.ts resolveWorkspaceAttributeSources).
 * Empty until read, and empty where nothing is connected. The track's Criteria & scales
 * column and S4 use it to say "the record, kept by Jira".
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchConnectorConfigsFromDB, getConnectorDefinition, type ConnectorConfigDB, type ConnectorType } from '@/lib/connectors';
import { keeperOf, resolveWorkspaceAttributeSources, type FieldSource } from '@/lib/itemFieldSources';

export interface AttributeKeepers {
  /** Every system that carries the attribute. */
  sources: Record<string, FieldSource[]>;
  /** The system that keeps the attribute's value, by key; "Jira". */
  keeperName: (attributeKey: string) => string | null;
  loaded: boolean;
}

const NONE: AttributeKeepers = { sources: {}, keeperName: () => null, loaded: false };

export function useAttributeKeepers(workspaceId: string | null | undefined): AttributeKeepers {
  const [configs, setConfigs] = useState<ConnectorConfigDB[] | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setConfigs([]);
      return;
    }
    let live = true;
    setConfigs(null);
    fetchConnectorConfigsFromDB(workspaceId)
      .then(({ data }) => live && setConfigs(data ?? []))
      .catch(() => live && setConfigs([]));
    return () => {
      live = false;
    };
  }, [workspaceId]);

  return useMemo(() => {
    if (configs === null) return NONE;
    const sources = resolveWorkspaceAttributeSources(configs, (t) => getConnectorDefinition(t as ConnectorType)?.name ?? t);
    return {
      sources,
      keeperName: (key: string) => keeperOf(sources[key])?.systemName ?? null,
      loaded: true,
    };
  }, [configs]);
}
